import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';

import { MetaCostAnalysis, CostAnalysisMetadata } from '../../../../../generated.commonjs/cost_analysis_pb';

interface TokenBreakdownItem {
  label: string;
  tokens: number;
  cost: number;
  rate: number;
  icon: string;
}

interface ModelCostEntry {
  model: string;
  cost: CostAnalysisMetadata.AsObject;
  breakdown: TokenBreakdownItem[];
}

/** Renders a cost-analysis summary card showing token usage, model costs, and per-step breakdowns for a completed task. */
@Component({
  selector: 'app-cost-analysis-display',
  imports: [MatCardModule, MatIconModule, MatChipsModule, MatTooltipModule, MatExpansionModule],
  templateUrl: './cost-analysis-display.component.html',
  styleUrls: ['./cost-analysis-display.component.scss'],
})
export class CostAnalysisDisplayComponent implements OnChanges {
  @Input() costAnalysis: MetaCostAnalysis.AsObject | null = null;
  @Input() displayMode: 'compact' | 'detailed' | 'summary' = 'summary';
  @Input() showBreakdown: boolean = false;

  modelCosts: ModelCostEntry[] = [];
  multiModel = false;
  efficiencyRating = 'unknown';
  efficiencyIcon = 'help';
  efficiencyColorClass = 'cost-unknown';
  costSummary = 'No cost data';
  costPerToken = 'N/A';
  processingInfo = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['costAnalysis'] || changes['displayMode'] || changes['showBreakdown']) {
      this.recompute();
    }
  }

  private recompute(): void {
    this.modelCosts = this.computeModelCosts();
    this.multiModel = this.modelCosts.length > 1;
    this.efficiencyRating = this.computeEfficiencyRating();
    this.efficiencyIcon = this.computeEfficiencyIcon();
    this.efficiencyColorClass = this.computeEfficiencyColorClass();
    this.costSummary = this.computeCostSummary();
    this.costPerToken = this.computeCostPerToken();
    this.processingInfo = this.computeProcessingInfo();
  }

  formatCost(cost: number): string {
    if (cost < 0.01) {
      return `$${cost.toFixed(6)}`;
    } else if (cost < 1) {
      return `$${cost.toFixed(4)}`;
    } else {
      return `$${cost.toFixed(2)}`;
    }
  }

  formatTokens(tokens: number): string {
    return tokens.toLocaleString();
  }

  formatRatePerMillion(ratePerMillion: number): string {
    return ratePerMillion.toFixed(2);
  }

  formatDuration(durationMs: number): string {
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}min`;
  }

  getModelCostPercentage(modelCost: number): number {
    if (!this.costAnalysis?.totalCostUsd || this.costAnalysis.totalCostUsd === 0) return 0;
    return (modelCost / this.costAnalysis.totalCostUsd) * 100;
  }

  private computeModelCosts(): ModelCostEntry[] {
    if (!this.costAnalysis?.modelCostsMap) return [];

    return this.costAnalysis.modelCostsMap
      .filter(([model, cost]) => model && model.trim().length > 0 && cost)
      .map(([model, cost]) => ({
        model,
        cost,
        breakdown: this.computeTokenBreakdown(cost),
      }));
  }

  private computeTokenBreakdown(cost: CostAnalysisMetadata.AsObject): TokenBreakdownItem[] {
    if (!cost.tokenBreakdown) return [];

    const breakdown = cost.tokenBreakdown;
    const items: TokenBreakdownItem[] = [];

    if (breakdown.nonCachedInput && breakdown.nonCachedInput.tokenCount > 0) {
      items.push({
        label: 'Input tokens',
        tokens: breakdown.nonCachedInput.tokenCount,
        cost: breakdown.nonCachedInput.costUsd || 0,
        rate: breakdown.nonCachedInput.ratePerMillion || 0,
        icon: 'input',
      });
    }

    if (breakdown.cachedContent && breakdown.cachedContent.tokenCount > 0) {
      items.push({
        label: 'Cached content',
        tokens: breakdown.cachedContent.tokenCount,
        cost: breakdown.cachedContent.costUsd || 0,
        rate: breakdown.cachedContent.ratePerMillion || 0,
        icon: 'cached',
      });
    }

    if (breakdown.output && breakdown.output.tokenCount > 0) {
      items.push({
        label: 'Output tokens',
        tokens: breakdown.output.tokenCount,
        cost: breakdown.output.costUsd || 0,
        rate: breakdown.output.ratePerMillion || 0,
        icon: 'output',
      });
    }

    if (breakdown.thinking && breakdown.thinking.tokenCount > 0) {
      items.push({
        label: 'Thinking tokens',
        tokens: breakdown.thinking.tokenCount,
        cost: breakdown.thinking.costUsd || 0,
        rate: breakdown.thinking.ratePerMillion || 0,
        icon: 'psychology',
      });
    }

    if (breakdown.toolUse && breakdown.toolUse.tokenCount > 0) {
      items.push({
        label: 'Tool use tokens',
        tokens: breakdown.toolUse.tokenCount,
        cost: breakdown.toolUse.costUsd || 0,
        rate: breakdown.toolUse.ratePerMillion || 0,
        icon: 'build',
      });
    }

    return items;
  }

  private computeEfficiencyRating(): string {
    if (!this.costAnalysis) return 'unknown';
    const cost = this.costAnalysis.totalCostUsd || 0;
    const tokens = this.costAnalysis.totalTokens || 0;
    if (tokens === 0) return 'unknown';
    const costPerMillion = (cost / tokens) * 1000000;
    if (costPerMillion < 0.5) return 'excellent';
    if (costPerMillion < 1.0) return 'good';
    if (costPerMillion < 2.0) return 'moderate';
    return 'expensive';
  }

  private computeEfficiencyIcon(): string {
    switch (this.efficiencyRating) {
      case 'excellent':
        return 'eco';
      case 'good':
        return 'thumb_up';
      case 'moderate':
        return 'info';
      case 'expensive':
        return 'warning';
      default:
        return 'help';
    }
  }

  private computeEfficiencyColorClass(): string {
    switch (this.efficiencyRating) {
      case 'excellent':
        return 'cost-excellent';
      case 'good':
        return 'cost-good';
      case 'moderate':
        return 'cost-moderate';
      case 'expensive':
        return 'cost-expensive';
      default:
        return 'cost-unknown';
    }
  }

  private computeCostSummary(): string {
    if (!this.costAnalysis) return 'No cost data';
    const cost = this.costAnalysis.totalCostUsd || 0;
    const tokens = this.costAnalysis.totalTokens || 0;
    if (this.multiModel) {
      return `${this.modelCosts.length} models: ${this.formatTokens(tokens)} tokens, ${this.formatCost(cost)}`;
    }
    const model = this.costAnalysis.primaryModel || 'Unknown';
    return `${model}: ${this.formatTokens(tokens)} tokens, ${this.formatCost(cost)}`;
  }

  private computeCostPerToken(): string {
    if (!this.costAnalysis?.totalTokens || this.costAnalysis.totalTokens === 0) return 'N/A';
    const costPerMillion = (this.costAnalysis.totalCostUsd / this.costAnalysis.totalTokens) * 1000000;
    return `$${costPerMillion.toFixed(2)}/M tokens`;
  }

  private computeProcessingInfo(): string {
    if (!this.costAnalysis) return '';
    const duration = this.costAnalysis.totalDurationMs || 0;
    const items = this.costAnalysis.totalItemsAnalyzed || 0;
    if (duration === 0 || items === 0) return '';
    const rate = (items * 60000) / duration;
    return `${items} items in ${this.formatDuration(duration)} (${rate.toFixed(1)}/min)`;
  }
}
